import { Link } from 'react-router'
import { Star, CheckCircle, Clock, MapPin, ArrowRight, Shield, MessageSquare, BadgeCheck } from 'lucide-react'

export interface ProCardPro {
  id: string
  slug: string
  companyName: string
  city: string
  ratingAvg: string | null
  reviewCount: number
  identityVerified: boolean
  insuranceVerified?: boolean
  responseTimeAvgHours?: string | null
  shortDescription?: string | null
  yearsExperience?: number | null
  logoUrl?: string | null
}

/**
 * Production-grade pro card for listings.
 * 3 visual zones: avatar/photo | identity+meta | actions.
 * Dual CTA matches competitor practice (urgencedépannage): direct action
 * button + primary profile link.
 *
 * Avatar cascade: logoUrl → company initial in gradient tile (no ugly
 * placeholder PNGs needed).
 *
 * Variant `density`:
 *   - "comfortable" (default) — larger, for /comparatif-artisans
 *   - "compact" — smaller, for /services/$cat/$city lists
 */
interface ProCardProps {
  pro: ProCardPro
  density?: 'comfortable' | 'compact'
  devisHref?: string // CTA target for "Demander devis"
  position?: number // 1-based rank for ordered lists
}

export function ProCard({
  pro,
  density = 'comfortable',
  devisHref,
  position,
}: ProCardProps) {
  const compact = density === 'compact'
  const avatarSize = compact ? 'h-12 w-12 text-base' : 'h-16 w-16 text-xl'
  const pad = compact ? 'p-4' : 'p-5'
  const rating = pro.ratingAvg ? parseFloat(pro.ratingAvg) : 0
  const response = pro.responseTimeAvgHours ? parseFloat(pro.responseTimeAvgHours) : null
  const devisTo = devisHref ?? `/demande-devis/1?pro=${pro.slug}`

  return (
    <article
      className={`group relative flex items-start gap-4 rounded-2xl border border-border bg-white ${pad} shadow-xs transition-shadow hover:shadow-md`}
    >
      {position !== undefined && (
        <span className="absolute -left-3 -top-3 flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white shadow-sm">
          #{position}
        </span>
      )}

      {/* Avatar / logo */}
      {pro.logoUrl ? (
        <img
          src={pro.logoUrl}
          alt={pro.companyName}
          loading="lazy"
          width={compact ? 48 : 64}
          height={compact ? 48 : 64}
          className={`${avatarSize} shrink-0 rounded-xl object-cover ring-1 ring-slate-100`}
        />
      ) : (
        <div
          className={`${avatarSize} flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-blue-400/10 font-bold text-primary`}
        >
          {pro.companyName.charAt(0).toUpperCase()}
        </div>
      )}

      {/* Identity + meta */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Link
            to={`/pro/${pro.slug}`}
            className="font-semibold text-slate-900 hover:text-primary"
          >
            {pro.companyName}
          </Link>
          {pro.identityVerified && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              <BadgeCheck className="h-3 w-3" /> Vérifié
            </span>
          )}
          {pro.insuranceVerified && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-primary">
              <Shield className="h-3 w-3" /> Assuré
            </span>
          )}
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {rating > 0 && pro.reviewCount > 0 ? (
            <span className="flex items-center gap-1">
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              <strong className="text-slate-900">{rating.toFixed(1)}</strong>
              <span className="text-slate-400">({pro.reviewCount} avis)</span>
            </span>
          ) : (
            <span className="text-slate-400">Nouveau sur AfficheServices</span>
          )}

          <span className="flex items-center gap-1 text-slate-500">
            <MapPin className="h-3.5 w-3.5" /> {pro.city}
          </span>

          {response !== null && !isNaN(response) && (
            <span className="flex items-center gap-1 text-emerald-600">
              <Clock className="h-3.5 w-3.5" />
              Répond ~{response < 1 ? `${Math.round(response * 60)} min` : `${Math.round(response)}h`}
            </span>
          )}

          {pro.yearsExperience && pro.yearsExperience > 2 && (
            <span className="flex items-center gap-1 text-slate-500">
              <CheckCircle className="h-3.5 w-3.5" />
              {pro.yearsExperience} ans d'expérience
            </span>
          )}
        </div>

        {pro.shortDescription && !compact && (
          <p className="mt-2 line-clamp-2 text-xs text-slate-500">{pro.shortDescription}</p>
        )}

        {/* Actions row — sm and up */}
        <div className="mt-3 hidden flex-wrap gap-2 sm:flex">
          <Link
            to={`/pro/${pro.slug}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary"
          >
            Voir le profil <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to={devisTo}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-hover"
          >
            <MessageSquare className="h-3 w-3" /> Demander un devis
          </Link>
        </div>
      </div>

      {/* Mobile-only tap target */}
      <Link
        to={`/pro/${pro.slug}`}
        className="absolute inset-0 sm:hidden"
        aria-label={`Voir ${pro.companyName}`}
      />
    </article>
  )
}
